import { useState, type ReactNode, type FormEvent } from "react";
import { router, usePage } from "@inertiajs/react";
import { AuthLayout } from "~/layouts/auth-layout";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import { Alert, AlertDescription } from "@components/ui/alert";
import rocketLaunch from "@assets/rocket-launch.png";

type ResetPasswordProps = {
  csrf_token: string;
  reset_password_token: string;
  minimum_password_length: number;
  errors?: Record<string, string[]>;
};

function ResetPassword() {
  const { reset_password_token, minimum_password_length, errors: serverErrors } =
    usePage<ResetPasswordProps>().props;

  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [errors, setErrors] = useState<Record<string, string[]>>(serverErrors || {});
  const [processing, setProcessing] = useState(false);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setProcessing(true);
    setErrors({});

    router.put("/users/password", {
      user: {
        reset_password_token,
        password,
        password_confirmation: passwordConfirmation,
      }
    }, {
      onError: (errs) => setErrors(errs as unknown as Record<string, string[]>),
      onFinish: () => setProcessing(false),
    });
  }

  const errorMessage = errors?.reset_password_token?.[0] || errors?.base?.[0];
  const passwordError = errors?.password?.[0];
  const confirmError = errors?.password_confirmation?.[0];

  return (
    <div className="flex w-full max-w-[508px] flex-col items-center">
      <img src={rocketLaunch} alt="" className="mb-6 h-48 w-48" />

      <h1 className="font-serif text-4xl font-semibold leading-10 text-base-500">
        Set your password
      </h1>
      <p className="mt-3 max-w-[589px] text-center font-sans text-lg leading-[22px] text-base-500 opacity-70">
        Choose a new password for your account.
      </p>

      <form onSubmit={handleSubmit} className="mt-8 flex w-full flex-col gap-4">
        {errorMessage && (
          <Alert variant="destructive">
            <AlertDescription>
              {errorMessage === "is invalid" ? "This reset link has expired. Please request a new one." : errorMessage}
            </AlertDescription>
          </Alert>
        )}

        <div>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-12 rounded-lg"
            placeholder="New password"
          />
          {passwordError && (
            <p className="mt-1 text-sm text-red-500">{passwordError}</p>
          )}
          {minimum_password_length && (
            <p className="mt-1 text-sm text-base-500 opacity-70">
              {minimum_password_length} characters minimum
            </p>
          )}
        </div>

        <div>
          <Input
            id="password_confirmation"
            type="password"
            autoComplete="new-password"
            value={passwordConfirmation}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
            className="h-12 rounded-lg"
            placeholder="Confirm new password"
          />
          {confirmError && (
            <p className="mt-1 text-sm text-red-500">{confirmError}</p>
          )}
        </div>

        <Button
          type="submit"
          disabled={processing}
          className="h-12"
        >
          {processing ? "Saving..." : "Set password"}
        </Button>
      </form>
    </div>
  );
}

ResetPassword.layout = (page: ReactNode) => <AuthLayout>{page}</AuthLayout>;

export default ResetPassword;
